import { authAppUrl } from "@/config/authAppUrl";
import { LazoPaths } from "@/enums/LazoPaths";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useLogout } from "@/hooks/queries/auth/useAuthQueries";
import { isNewLoginEnabled, loadFeatureFlags } from "@/utils/featureFlagsRuntime";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export interface LogoutProps {
  /** Test seam. When provided, used in place of `window.location.replace`. */
  navigateExternal?: (url: string) => void;
  /** Test seam. When provided, used in place of `window.location.origin`. */
  currentOrigin?: string;
}

export const Logout = ({ navigateExternal, currentOrigin }: LogoutProps) => {
  const navigate = useNavigate();
  const { mutateAsync } = useLogout();

  const logout = useCallback(async () => {
    try {
      // Expire the HttpOnly .lazo.one cookie and revoke the token server-side.
      // The cookie is sent automatically; JS cannot clear it on its own.
      await mutateAsync();
    } catch {
      // Even if revocation fails, still clear the client and redirect so the
      // user is never left looking logged in.
    } finally {
      // logOut() first so its LOGOUT observer event fires while keys still exist.
      AuthService.logOut();
      // Full wipe — drops everything logOut() misses (e, tenant/workspace state,
      // signup forms, tour flags, sidebar).
      localStorage.clear();
      sessionStorage.clear();
      // With the new login live, this half only cleared the lazo-web session —
      // the standalone auth-api `lazo_session` is still alive, and the index's
      // signed-out bounce would land on the auth app, whose sign-in probe
      // re-mints the lazo-web cookie and forwards the person straight back in,
      // signed in. Hand off to auth/app's /logout (same contract as NewLogout)
      // so BOTH sessions die. Flag read never rejects and fails closed to the
      // legacy index redirect; App's bootstrap warmed the cache, so this is
      // normally an already-resolved promise.
      await loadFeatureFlags();
      if (isNewLoginEnabled()) {
        const origin = currentOrigin ?? window.location.origin;
        const target = `${authAppUrl()}/logout?returnTo=${encodeURIComponent(origin)}`;
        if (navigateExternal) {
          navigateExternal(target);
        } else {
          window.location.replace(target);
        }
      } else {
        navigate(LazoPaths.INDEX);
      }
    }
  }, [mutateAsync, navigate, navigateExternal, currentOrigin]);

  useEffect(() => {
    logout();
  }, [logout]);

  return <>Logout</>;
};
